import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DataListPage from "../../../components/DataListPage";
import authService from "../../../services/auth";
import rolePermissionService from "../../../services/rolePermissionService";
import { instanceToken } from "../../../utils/constant";
import { AuthContext } from "../../../context/AuthContext";

const passwordHint =
  "Min 8 chars, with upper, lower, digit, and special (@$!%*?&)";

export default function DashboardUsers({ homeAlert }) {
  const currentUser = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    role: "",
  });
  const [editForm, setEditForm] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const loadRoles = useCallback(async () => {
    try {
      const res = await rolePermissionService.getAll(instanceToken.token);
      const codes =
        res.data.roleCodes ||
        (Array.isArray(res.data.roles)
          ? res.data.roles.map((r) => r.role)
          : Object.keys(res.data.permissions || {}));
      setRoleOptions(codes);
    } catch (error) {
      console.error(error);
      setRoleOptions([]);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authService.getAuths(instanceToken.token);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      homeAlert?.(
        error?.response?.data?.error || "Failed to load dashboard users",
        true
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [homeAlert]);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [loadUsers, loadRoles]);

  const defaultRole =
    roleOptions.find((r) => r === "CLAIM") || roleOptions[0] || "";

  const roleChoicesFor = (currentRole) => {
    const set = new Set(roleOptions);
    if (currentRole) set.add(currentRole);
    return [...set].sort();
  };

  const filtered = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      String(u.username || "")
        .toLowerCase()
        .includes(q) ||
      String(u.role || "")
        .toLowerCase()
        .includes(q)
    );
  });

  const openCreate = () => {
    setCreateForm({
      username: "",
      password: "",
      role: defaultRole,
    });
    setFormErrors({});
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setEditForm({
      id: row.id,
      username: row.username || "",
      password: "",
      role: row.role || defaultRole,
      active: Boolean(row.active),
    });
    setFormErrors({});
    setEditOpen(true);
  };

  const validateCreate = () => {
    const err = {};
    if (!createForm.username.trim()) err.username = "Username is required";
    if (!createForm.password) err.password = "Password is required";
    if (!createForm.role) err.role = "Role is required";
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const validateEdit = () => {
    const err = {};
    if (!editForm?.username?.trim()) err.username = "Username is required";
    if (!editForm?.role) err.role = "Role is required";
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    setSaving(true);
    try {
      await authService.postAuth(instanceToken.token, {
        username: createForm.username.trim(),
        password: createForm.password,
        role: createForm.role,
      });
      homeAlert?.("User created successfully", false);
      setCreateOpen(false);
      await loadUsers();
    } catch (error) {
      console.error(error);
      const msg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        "Failed to create user";
      homeAlert?.(typeof msg === "string" ? msg : "Failed to create user", true);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!validateEdit()) return;
    setSaving(true);
    try {
      const payload = {
        username: editForm.username.trim(),
        role: editForm.role,
        active: editForm.active,
      };
      if (editForm.password) {
        payload.password = editForm.password;
      }
      await authService.putAuthById(
        instanceToken.token,
        editForm.id,
        payload
      );
      homeAlert?.("User updated successfully", false);
      setEditOpen(false);
      await loadUsers();
    } catch (error) {
      console.error(error);
      const msg =
        error?.response?.data?.error?.message ||
        error?.response?.data?.error ||
        "Failed to update user";
      homeAlert?.(typeof msg === "string" ? msg : "Failed to update user", true);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    const nextActive = !row.active;
    if (
      !nextActive &&
      currentUser?.id &&
      Number(currentUser.id) === Number(row.id)
    ) {
      homeAlert?.("You cannot deactivate your own account", true);
      return;
    }
    try {
      await authService.putAuthById(instanceToken.token, row.id, {
        active: nextActive,
      });
      homeAlert?.(
        nextActive ? "User activated" : "User deactivated",
        false
      );
      await loadUsers();
    } catch (error) {
      console.error(error);
      homeAlert?.(
        error?.response?.data?.error || "Failed to update status",
        true
      );
    }
  };

  const columns = [
    { id: "id", label: "ID", render: (row) => row.id },
    { id: "username", label: "Username", render: (row) => row.username },
    {
      id: "role",
      label: "Role",
      render: (row) => (
        <Chip size="small" label={row.role || "—"} color="primary" variant="outlined" />
      ),
    },
    {
      id: "active",
      label: "Status",
      render: (row) => (
        <Chip
          size="small"
          label={row.active ? "Active" : "Inactive"}
          color={row.active ? "success" : "default"}
        />
      ),
    },
    {
      id: "createdAt",
      label: "Created",
      render: (row) =>
        row.createdAt
          ? new Date(row.createdAt).toLocaleString()
          : "—",
    },
  ];

  return (
    <>
      <DataListPage
        title="Dashboard Users"
        rows={filtered}
        columns={columns}
        count={filtered.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(+e.target.value);
          setPage(0);
        }}
        loading={loading}
        createButton={{
          label: "Create User",
          onClick: openCreate,
        }}
        search={{
          value: search,
          placeholder: "Search username or role",
          live: true,
          onSearch: (value) => {
            setSearch(value);
            setPage(0);
          },
        }}
        menuActions={[
          {
            label: "Edit",
            onClick: (row) => openEdit(row),
          },
          {
            label: "Activate / Deactivate",
            onClick: (row) => toggleActive(row),
          },
        ]}
      />

      <Dialog
        open={createOpen}
        onClose={() => !saving && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Dashboard User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Username"
              value={createForm.username}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, username: e.target.value }))
              }
              error={Boolean(formErrors.username)}
              helperText={formErrors.username}
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={createForm.password}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, password: e.target.value }))
              }
              error={Boolean(formErrors.password)}
              helperText={formErrors.password || passwordHint}
              fullWidth
            />
            <FormControl fullWidth error={Boolean(formErrors.role)}>
              <InputLabel id="create-role-label">Role</InputLabel>
              <Select
                labelId="create-role-label"
                label="Role"
                value={createForm.role}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, role: e.target.value }))
                }
              >
                {roleChoicesFor(createForm.role).map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={saving}
            onClick={handleCreate}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => !saving && setEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Dashboard User</DialogTitle>
        <DialogContent>
          {editForm && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="Username"
                value={editForm.username}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, username: e.target.value }))
                }
                error={Boolean(formErrors.username)}
                helperText={formErrors.username}
                fullWidth
              />
              <TextField
                label="New Password (optional)"
                type="password"
                value={editForm.password}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, password: e.target.value }))
                }
                helperText={passwordHint}
                fullWidth
              />
              <FormControl fullWidth error={Boolean(formErrors.role)}>
                <InputLabel id="edit-role-label">Role</InputLabel>
                <Select
                  labelId="edit-role-label"
                  label="Role"
                  value={editForm.role}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, role: e.target.value }))
                  }
                >
                  {roleChoicesFor(editForm.role).map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.active}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        active: e.target.checked,
                      }))
                    }
                    disabled={
                      currentUser?.id &&
                      Number(currentUser.id) === Number(editForm.id)
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    Active
                    {currentUser?.id &&
                    Number(currentUser.id) === Number(editForm.id)
                      ? " (cannot deactivate yourself)"
                      : ""}
                  </Typography>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={saving}
            onClick={handleEdit}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
